/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom";

import { API, graphqlOperation, Storage } from "aws-amplify";
import {
  reloadYardCapabilityByReloadYard,
  reloadYardCommodityByActiveBySortKey,
} from "../../../graphql/queries";
import {
  countInvoices,
  getInvoiceCounter,
} from "../../../graphql/reloadyard/custom-queries";
import {
  createPaymentTerm,
  deletePaymentTerm,
  updatePaymentTerm,
  updateReloadYardCapability,
  updateReloadYardCommodity,
} from "../../../graphql/mutations";
import {
  createRailEquipmentType,
  updateRailEquipmentType,
  createTruckEquipmentType,
  updateTruckEquipmentType,
} from "../../../graphql/mutations";

import {
  Backdrop,
  Box,
  CircularProgress,
  makeStyles,
  useMediaQuery,
} from "@material-ui/core";

import {
  Edit,
  FormDataConsumer,
  FormTab,
  Loading,
  SaveButton,
  TabbedForm,
  Toolbar,
  TopToolbar,
  useEditController,
  useNotify,
  useRedirect,
} from "react-admin";
import { useForm } from "react-final-form";
import setFieldData from "final-form-set-field-data";

import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { HelpButton } from "../../../components/common/HelpButton";
import { NotesTab } from "../../../components/common/NotesTab";
import { NewBadge } from "../../../components/common/FeatureBadges";

import {
  isAdmin,
  logError,
  retrieveStripeSubscription,
  setupCommodityMaps,
  setupLocationMaps,
} from "../../../utils/appUtils";
import {
  getDirFileKey,
  saveRemoveFile,
  saveRemoveFiles,
} from "../../../utils/fileUtils";
import {
  FilePaths,
  NoteType,
  PaymentTermType,
  SORTKEY_BUFFER,
  usHolidays,
  VALIDATION_MSG,
} from "../../../utils/constants";
import {
  coerceFormArrayField,
  decodeHolidaysInSchedule,
  encodeScheduleHolidaysForMutation,
} from "../../../utils/holidayRules";
import { getLastValueFromUrl } from "../../../utils/utils";
import Subscription from "../../../adminApp/resources/reloadYards/Subscription";
import { ReloadYardHelp } from "./ReloadYardHelp";
import { ContactInfo } from "./ContactInfo";
import { LoadTypesPanel } from "./LoadTypesPanel";
import { LayoutTab } from "./layout/LayoutTab";
import YardSettingsTab from "./YardSettingsTab";
import { buildGeofencePolygon } from "./GeoFencePanel";
import { ServicesTab } from "./services/ServicesTab";
import { buildReloadYardValidationErrors } from "./ReloadYardValidation";

const _ = require("lodash");

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

export const MyYardEdit = (props) => {
  return (
    <Edit
      {...props}
      title={<ForceTitle title="Edit Yard" />}
      mutationMode="pessimistic"
      actions={<YardEditActions {...props} />}
    >
      <TabbedFormContainer {...props} />
    </Edit>
  );
};

const buildReloadYardFormRecord = (record) => {
  if (!record) {
    return record;
  }

  const next = { ...record };
  if (next.schedule) {
    next.schedule = decodeHolidaysInSchedule(
      { ...next.schedule },
      usHolidays,
    );
  }
  next.notes = coerceFormArrayField(next.notes);
  next.taxRates = coerceFormArrayField(next.taxRates);
  next.driverStops = coerceFormArrayField(next.driverStops);
  next.fileCategories = coerceFormArrayField(next.fileCategories);
  next.paymentTerms = {
    ...(next.paymentTerms || {}),
    items: coerceFormArrayField(next.paymentTerms?.items),
  };

  return {
    ...next,
    railEquipmentTypes: {
      items: coerceFormArrayField(next.railEquipmentTypes?.items).sort(
        (a, b) => a.sortKey - b.sortKey,
      ),
    },
    truckEquipmentTypes: {
      items: coerceFormArrayField(next.truckEquipmentTypes?.items).sort(
        (a, b) => a.sortKey - b.sortKey,
      ),
    },
  };
};

const TabbedFormContainer = (props) => {
  const notify = useNotify();
  const isComponentMounted = useRef(true);
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const { record } = useEditController(props);
  const [commodities, setCommodities] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [isLoaded, setIsLoaded] = useState();
  const [allServices, setAllServices] = useState([]);
  const location = useLocation();
  const history = useHistory();
  const formRecord = useMemo(() => buildReloadYardFormRecord(record), [record]);

  useEffect(() => {
    if (isComponentMounted) {
      getCommCapaData();
      if (record?.subscription) retrieveStripeSubscription(record);
    }
    return () => (isComponentMounted.current = false);
  }, [record]);

  useEffect(() => {
    // clear the ?t= parameter when not on tab with no vertical tabs
    const lastPath = getLastValueFromUrl(location.pathname);
    const hasVerticalTabs = ["layout", "settings"].includes(lastPath);
    const searchParams = new URLSearchParams(location.search);
    const hasTabParam = searchParams.has("t");

    if (!hasVerticalTabs && hasTabParam) {
      searchParams.delete("t");
      history.replace({
        pathname: location.pathname,
        search: searchParams.toString() ? `?${searchParams.toString()}` : "",
      });
    }
  }, [location.pathname, location.search, history]);

  const getCommCapaData = async () => {
    try {
      if (isLoaded) return;
      setIsLoaded(true);
      let res = await API.graphql(
        graphqlOperation(reloadYardCommodityByActiveBySortKey, {
          reloadYardId: record.id,
        }),
      );
      let data = res.data.reloadYardCommodityByActiveBySortKey.items;
      data = _.orderBy(data, ["sortKey"], ["asc"]);
      setCommodities([...data]);
      res = await API.graphql(
        graphqlOperation(reloadYardCapabilityByReloadYard, {
          reloadYardId: record.id,
        }),
      );
      data = res.data.reloadYardCapabilityByReloadYard.items;
      data = _.orderBy(data, ["capability.sortKey"], ["asc"]);
      setCapabilities(data);
    } catch (err) {
      logError("resources:reloadYard:MyYard:getData", err, notify);
      setIsLoaded(false); // allow retry on next effect
    }
  };

  const validateForm = buildReloadYardValidationErrors;

  const handleCommodityChange = async (evt, selectedItm) => {
    debugger;
    try {
      const toUpdateCommodity = { ...selectedItm };
      delete toUpdateCommodity.commodity;
      delete toUpdateCommodity.createdAt;
      delete toUpdateCommodity.updatedAt;
      const response = await API.graphql(
        graphqlOperation(updateReloadYardCommodity, {
          input: toUpdateCommodity,
        }),
      );
      const yardCommodity = response.data.updateReloadYardCommodity;

      const newCommodities = commodities.map((itm) =>
        itm.id === yardCommodity.id ? yardCommodity : itm,
      );
      setCommodities(newCommodities);
      const commodityMap = {};
      newCommodities.forEach((itm) => (commodityMap[itm.id] = itm));
      window.appUser.reloadYard.yardCommodities = { items: newCommodities };
      window.appUser.reloadYard.activeYardCommodities = {
        items: newCommodities.filter((itm) => itm.active === "true"),
      };
      window.appUser.reloadYard.yardCommodityMap = commodityMap;

      notify("Load Type updated succesfully.", "info");
    } catch (err) {
      logError(
        "resources:reloadYard:MyYard:handleCommodityChange",
        err,
        notify,
      );
    }
  };

  const handleValueChange = async (evt, selectedItm, saveFlag) => {
    try {
      setCapabilities(
        capabilities.map((itm) =>
          itm.id === selectedItm.id
            ? { ...itm, requiredValue: evt.target.value }
            : itm,
        ),
      );

      debugger;
      if (saveFlag) {
        // consider calling handleCapabilityChange (evt, {...selectedItm, requiredValue: evt.target.value}, notifyFlag=false)
        const toUpdateCapability = {
          ...selectedItm,
          requiredValue: evt.target.value,
        };
        delete toUpdateCapability.capability;
        delete toUpdateCapability.createdAt;
        delete toUpdateCapability.updatedAt;
        await API.graphql(
          graphqlOperation(updateReloadYardCapability, {
            input: toUpdateCapability,
          }),
        );
      }
    } catch (err) {
      logError("resources:reloadYard:MyYard:handleValueChange", err, notify);
    }
  };

  if (!formRecord) return <Loading />;
  return (
    <TabbedForm
      {...props}
      record={formRecord}
      toolbar={<MyYardEditToolbar />}
      validate={validateForm}
      submitOnEnter={false}
      mutators={{ setFieldData }}
      initialValues={formRecord}
      // warnWhenUnsavedChanges
    >
      <FormTab label={"Contact Info"}>
        <ContactInfo {...props} />
      </FormTab>
      {!isXSmall && (
        <FormTab path="loadTypes" label="Load Types">
          <LoadTypesPanel
            commodities={commodities}
            setCommodities={handleCommodityChange}
            handleChange={handleValueChange}
          />
        </FormTab>
      )}
      {!isXSmall && (
        <FormTab
          path="services"
          // label="Service Rates"
          label={
            <span>
              Service Rates <NewBadge />
            </span>
          }
        >
          <ServicesTab
            {...props}
            allServices={allServices}
            setAllServices={setAllServices}
            reloadyard={formRecord}
          />
        </FormTab>
      )}
      <FormTab path="layout" label="Layout">
        <Box flex={1} mt={"2em"} style={{ width: "100%" }}>
          <Box display={{ xs: "block", sm: "flex" }}>
            <LayoutTab {...props} record={formRecord} />
          </Box>
        </Box>
      </FormTab>
      {!isXSmall && (
        <FormTab
          path="settings"
          // label="Settings"
          label={
            <span>
              Settings <NewBadge />
            </span>
          }
        >
          <Box flex={1} mt={"2em"} style={{ width: "100%" }}>
            <YardSettingsTab {...props} record={formRecord} />
          </Box>
        </FormTab>
      )}
      {isAdmin() && (
        <FormTab path="subscription" label="Subscription">
          <Subscription {...props} />
        </FormTab>
      )}
      {isAdmin() && (
        <FormTab path="notes" label="Notes">
          <NotesTab />
        </FormTab>
      )}
    </TabbedForm>
  );
};

const YardEditActions = ({ basePath, data, resource, ...props }) => {
  if (!data) return null;
  return (
    <TopToolbar>
      <HelpButton>
        <ReloadYardHelp {...props} />
      </HelpButton>
      <BackButton basePath={`/reloadYards/${data.id}/show`} />
    </TopToolbar>
  );
};

const MyYardEditToolbar = ({ record, ...props }) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const [saving, setSaving] = useState(false);

  const removeImageFile = async (record, data, source) => {
    if (record[source]) {
      const key = record[source].key;
      if (key) await Storage.remove(key);

      if (data) {
        data[source] = null;
        window.appUser.reloadYard[source] = null;
      }
    }
  };

  const saveTransformImage = async (record, data, source, dir) => {
    debugger;
    if (!data[source]) await removeImageFile(record, data, source);
    else if (data[source] && !data[source].key) {
      if (record[source]) await removeImageFile(record, null, source);

      const key = getDirFileKey(data[source].rawFile, dir);
      let response = await Storage.put(key, data[source].rawFile, {
        level: "public",
        contentType: data[source].rawFile.type,
      });

      if (response.key) {
        const s3Object = {
          bucket: API._options.aws_user_files_s3_bucket,
          region: API._options.aws_user_files_s3_bucket_region,
          key: response.key,
        };

        data[source] = s3Object;
        window.appUser.reloadYard[source] = s3Object;
      } else throw new Error(`Storage.put failed: ${key}`);
    }

    if (data[source]) {
      delete data[source].rawFile;
      delete data[source].title;
      delete data[source].src;
    }
  };

  const validateInvoiceCounterChange = async (data, form) => {
    debugger;

    try {
      // Fetch current counter from DB and invoice count in parallel for efficiency IMPORTANT: We use DB counter, not record.invoiceCounter, because:
      // 1. New invoices may have been created while user edits (stale data problem)
      // 2. Counter can be > active invoice count (due to void/inactive invoices)
      // 3. Multiple users/tabs can create invoices concurrently
      const [invoiceResponse, reloadYardResponse] = await Promise.all([
        // Check if ANY active invoices exist
        // We use this to determine if we're in "initial setup" mode vs "ongoing operation" mode
        API.graphql(
          graphqlOperation(countInvoices, {
            filter: { reloadYardId: { eq: record.id }, active: { eq: "true" } },
            limit: 1,
          }),
        ),
        // Get CURRENT invoiceCounter from DB (always use fresh value, not stale record data) This ensures we validate against the actual counter, even if it changed during editing
        API.graphql(graphqlOperation(getInvoiceCounter, { id: record.id })),
      ]);

      const invoiceCount = invoiceResponse?.data.searchInvoices.total ?? 0;
      const reloadYardData = reloadYardResponse?.data.getReloadYard;
      const currentCounter = reloadYardData.invoiceCounter ?? 0;

      // CASE: Initial setup - no invoices exist, user can set any counter. This allows setting initial counter to match external systems or restart numbering
      // Examples: New yard setup, switching from manual to system numbering, etc.
      if (invoiceCount === 0) {
        // No restrictions - user can set any counter value when no invoices exist
        form?.mutators?.setFieldData?.("invoiceCounter", undefined);
        return true; // valid
      }

      // CASE: Invoices exist - prevent rollback
      // Counter in DB represents the last used number. We must prevent setting a counter lower than this to avoid duplicate invoice numbers.
      // NOTE: Counter can be > invoiceCount due to void/inactive invoices being deleted. Example: 5 active invoices exist, but counter is 103 because 98 invoices were voided
      if (data.invoiceCounter < currentCounter) {
        // Prevent rollback - counter cannot be less than current DB value
        form.mutators.setFieldData("invoiceCounter", {
          error: true, // `Must be at least ${currentCounter}`
          newCounter: currentCounter,
        });
        return false; // invalid - No submission
      }

      // CASE: Valid - counter is >= currentCounter. User can advance counter (e.g., set to 2000 when current is 103)
      // This allows skipping number ranges or adjusting for external systems. Examples: Resuming after manual invoices, syncing with external system, etc.
      form?.mutators?.setFieldData?.("invoiceCounter", undefined);
      return true; // valid - proceed with submission
    } catch (error) {
      setSaving(false);
      logError(
        "reloadYard:reloadYardEdit:validateInvoiceCounter",
        error,
        notify,
      );
      return false; // error - No submission
    }
  };

  const handleValidateSave = async (formData) => {
    setSaving(true);
    const isValid = await validateInvoiceCounterChange(formData, form);
    if (!isValid) {
      setSaving(false);
      notify(VALIDATION_MSG, "warning");
    }
    props.handleSubmitWithRedirect(); // trigger form validation
    // A failed validation aborts the submit before the save runs, so neither
    // onSuccess nor onFailure fires and the backdrop would spin forever over a
    // form the user can no longer reach. Clear it ourselves.
    if (form.getState().invalid) setSaving(false);
  };

  /**
   * Saves payment terms by creating new ones or updating changed existing ones.
   *
   * @param {object} record - The original record.
   * @param {object} data - The form data object containing id and paymentTerms.
   */
  const saveRemovePaymentTerms = async (record, data) => {
    const updatedTerms = data?.paymentTerms?.items ?? [];
    const originalTerms = record?.paymentTerms?.items ?? [];
    const formState = form.getState();

    const isDirty = Object.keys(formState.dirtyFields).some((key) =>
      key.startsWith("paymentTerms"),
    );
    if (!isDirty) return;

    try {
      const promiseList = [];

      // === Handle create/update ===
      updatedTerms.forEach((term, idx) => {
        const input = {
          ...(term.id ? { id: term.id } : {}),
          groups: data.groups,
          reloadYardId: data.id,
          name: term.name?.trim(),
          code: term.code?.trim(),
          type: term.type || PaymentTermType.DUE_DAYS,
          dueDays: term.dueDays,
          // dueDays: term.type === "DUE_DAYS" ? term.dueDays : null,
          // dayOfMonthDue: term.type === "STANDARD_DUE_DATE" ? term.dayOfMonthDue : null,
          sortKey: SORTKEY_BUFFER + idx,
        };

        const op = term.id
          ? API.graphql(graphqlOperation(updatePaymentTerm, { input }))
          : API.graphql(graphqlOperation(createPaymentTerm, { input }));

        promiseList.push(op);
      });

      // === Handle deletions ===
      const updatedIds = new Set(
        updatedTerms.filter((t) => t.id).map((t) => t.id),
      );
      const deletedTerms = originalTerms.filter(
        (original) => original.id && !updatedIds.has(original.id),
      );

      deletedTerms.forEach((term) => {
        const input = { id: term.id };
        const op = API.graphql(graphqlOperation(deletePaymentTerm, { input }));
        promiseList.push(op);
      });

      // Run all operations in parallel
      await Promise.all(promiseList);
    } catch (err) {
      logError("reloadYard:reloadYardEdit:savePaymentTerms", err, notify);
    }
  };

  /**
   * Creates, updates, and deletes Rail and Truck Equipment Types for the yard.
   * Uses originals from the loaded record (no list queries required).
   */
  const saveRemoveEquipmentTypes = async (record, data) => {
    const formState = form.getState();
    const isRailDirty = Object.keys(formState.dirtyFields).some((key) =>
      key.startsWith("railEquipmentTypes"),
    );
    const isTruckDirty = Object.keys(formState.dirtyFields).some((key) =>
      key.startsWith("truckEquipmentTypes"),
    );
    if (!isRailDirty && !isTruckDirty) return;

    try {
      const originalRail = record?.railEquipmentTypes?.items ?? [];
      const originalTruck = record?.truckEquipmentTypes?.items ?? [];

      const updatedRail = data?.railEquipmentTypes?.items ?? [];
      const updatedTruck = data?.truckEquipmentTypes?.items ?? [];

      const ops = [];

      // Create/Update Rail
      (Array.isArray(updatedRail) ? updatedRail : []).forEach((item, idx) => {
        const input = {
          ...(item.id ? { id: item.id } : {}),
          active: "true",
          groups: data.groups,
          reloadYardId: data.id,
          name: item.name?.trim(),
          arrTypeCode: item.arrTypeCode?.trim() || null,
          length: !item.length ? null : Number(item.length),
          sortKey: SORTKEY_BUFFER + idx,
        };
        const op = item.id
          ? API.graphql(graphqlOperation(updateRailEquipmentType, { input }))
          : API.graphql(graphqlOperation(createRailEquipmentType, { input }));
        ops.push(op);
      });

      // Soft-delete Rail (set active="false")
      const updatedRailIds = new Set(
        (Array.isArray(updatedRail) ? updatedRail : [])
          .filter((i) => i.id)
          .map((i) => i.id),
      );
      (Array.isArray(originalRail) ? originalRail : [])
        .filter((orig) => orig.id && !updatedRailIds.has(orig.id))
        .forEach((toDel, idx) => {
          const input = {
            id: toDel.id,
            active: "false",
            sortKey: toDel.sortKey,
          };
          ops.push(
            API.graphql(graphqlOperation(updateRailEquipmentType, { input })),
          );
        });

      // Create/Update Truck
      (Array.isArray(updatedTruck) ? updatedTruck : []).forEach((item, idx) => {
        const input = {
          ...(item.id ? { id: item.id } : {}),
          active: "true",
          groups: data.groups,
          reloadYardId: data.id,
          name: item.name?.trim(),
          dotSpecCode: item.dotSpecCode?.trim() || null,
          dotClassCode: item.dotClassCode?.trim() || null,
          length: !item.length ? null : Number(item.length),
          sortKey: SORTKEY_BUFFER + idx,
        };
        const op = item.id
          ? API.graphql(graphqlOperation(updateTruckEquipmentType, { input }))
          : API.graphql(graphqlOperation(createTruckEquipmentType, { input }));
        ops.push(op);
      });

      // Soft-delete Truck (set active="false")
      const updatedTruckIds = new Set(
        (Array.isArray(updatedTruck) ? updatedTruck : [])
          .filter((i) => i.id)
          .map((i) => i.id),
      );
      (Array.isArray(originalTruck) ? originalTruck : [])
        .filter((orig) => orig.id && !updatedTruckIds.has(orig.id))
        .forEach((toDel, idx) => {
          const input = {
            id: toDel.id,
            active: "false",
            sortKey: toDel.sortKey,
          };
          ops.push(
            API.graphql(graphqlOperation(updateTruckEquipmentType, { input })),
          );
        });

      if (ops.length > 0) await Promise.all(ops);
    } catch (err) {
      logError("reloadYard:reloadYardEdit:saveEquipmentTypes", err, notify);
    }
  };

  const transform = async (data) => {
    data = { ...data };
    try {
      await saveRemovePaymentTerms(record, data);
      await saveRemoveEquipmentTypes(record, data);
      await saveTransformImage(record, data, "logoImage", FilePaths.LOGO);
      await saveTransformImage(record, data, "mapImage", FilePaths.MAPS);

      await saveRemoveFile(
        record,
        data,
        "bolTemplate",
        `${data.id}/${FilePaths.RELOADYARD}`,
      );
      await saveRemoveFile(
        record,
        data,
        "receiptTemplate",
        `${data.id}/${FilePaths.RELOADYARD}`,
      );

      // Used in Admin Portal
      await saveRemoveFiles(
        record,
        data,
        "attachments",
        `${data.id}/${FilePaths.RELOADYARD}`,
      );
    } catch (err) {
      logError("reloadYard:MyYardEditToolbar:transform", err, notify);
      return Promise.reject();
    }

    // Post-check-in stops: the label is what a gate row stores and what
    // driverApi matches on, so it goes out trimmed — a trailing space typed
    // here would otherwise never match the dropdown value saved on the queue.
    // Empty rows (all blank) are dropped; a half-filled one was already
    // refused by validateDriverStops. No rows at all is a yard with no
    // waiting area, and is stored as such.
    if (Array.isArray(data.driverStops)) {
      data.driverStops = data.driverStops
        .map((stop) => ({
          label: (stop?.label || "").trim(),
          message: (stop?.message || "").trim(),
        }))
        .filter((stop) => stop.label || stop.message);
    }
    if (typeof data.postCheckInMessage === "string")
      data.postCheckInMessage = data.postCheckInMessage.trim() || null;
    if (typeof data.externalCompanyId === "string")
      data.externalCompanyId = data.externalCompanyId.trim() || null;
    data.fileCategories = data.fileCategories?.filter((itm) => itm?.name);
    data.fileCategories = data.fileCategories?.map((itm) =>
      itm.key
        ? itm
        : { key: itm.name.toLowerCase().replace(/\s/g, ""), name: itm.name },
    );
    data.fileCategories =
      data.fileCategories?.length > 0 ? data.fileCategories : null;

    // Geo-fence: if enabled and no valid custom boundary set, auto-generate circle
    if (data.geofenceEnabled && data.latitude && data.longitude) {
      let hasValidGeofence = false;
      try {
        if (data.geofence) {
          JSON.parse(data.geofence);
          hasValidGeofence = true;
        }
      } catch {
        data.geofence = null; // discard invalid JSON
      }
      if (!hasValidGeofence) {
        const radius = data.geofenceToleranceMeters ?? 500;
        data.geofence = JSON.stringify(
          buildGeofencePolygon(data.latitude, data.longitude, radius),
        );
        data.geofenceToleranceMeters = radius;
      }
    } else if (!data.geofenceEnabled) {
      data.geofence = null;
    }

    data.state = data.state.toUpperCase();
    data.invoiceNumberPrefix = data.invoiceNumberPrefix.toUpperCase();

    if (data.schedule) {
      encodeScheduleHolidaysForMutation(data);
    }

    if (data.notes?.length > 0) {
      data.notes.forEach((element) => {
        if (!element.date) {
          element.type = NoteType.GENERIC;
          element.date = new Date();
          element.by = window.appUser?.fullName;
        }
      });
    } else data.notes = undefined;

    debugger;
    delete data.aisles;
    delete data.areas;
    delete data.checklists;
    delete data.documents;
    delete data.eventTypes;
    delete data.locations;
    delete data.mapAreaId;
    delete data.paymentTerms;
    delete data.railEquipmentTypes;
    delete data.truckEquipmentTypes;
    delete data.subscription; // updated by retrieveStripeSubscription
    delete data.yardCommodities;
    delete data.zones;

    return data;
  };

  const onSuccess = ({ data }) => {
    debugger;
    setupLocationMaps(data);
    setupCommodityMaps(data);
    window.appUser.reloadYard = data;
    notify("My Yard updated successfully", "success");
    redirect(`/reloadYards/${data.id}/show`);
    setSaving(false);
  };

  const onFailure = (error) => {
    setSaving(false);
    logError("reloadYard:MyYardEditToolbar:onFailure", error, notify);
  };

  const isPristine = () => {
    const formState = form.getState();
    return formState.pristine;
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <Toolbar {...props}>
          <Backdrop className={classes.backdrop} open={saving}>
            <CircularProgress color="inherit" />
          </Backdrop>
          <SaveButton
            transform={transform}
            onSuccess={onSuccess}
            onFailure={onFailure}
            disabled={isPristine()}
            handleSubmitWithRedirect={() => handleValidateSave(formData)}
          />
        </Toolbar>
      )}
    </FormDataConsumer>
  );
};
